import React from 'react';
import { ListItemButton, ListItemIcon, ListItemText, Typography, Chip, Box } from '@mui/material';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import type { SearchResult, ResultCategory } from './types';

const CATEGORY_ICONS: Record<ResultCategory, React.ReactNode> = {
  pinned: <PushPinOutlinedIcon fontSize="small" />,
  activeToday: <ScheduleIcon fontSize="small" />,
  myCases: <AssignmentIndOutlinedIcon fontSize="small" />,
  cases: <WorkOutlineIcon fontSize="small" />,
  contacts: <PersonOutlineIcon fontSize="small" />,
  customers: <BusinessOutlinedIcon fontSize="small" />,
  recentActivity: <HistoryIcon fontSize="small" />,
  navigation: <NavigateNextIcon fontSize="small" />,
};

interface CommandPaletteResultItemProps {
  result: SearchResult;
  index: number;
  isActive: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}

const CommandPaletteResultItem: React.FC<CommandPaletteResultItemProps> = ({
  result,
  index,
  isActive,
  onMouseEnter,
  onClick,
}) => {
  return (
    <ListItemButton
      data-index={index}
      id={`command-palette-result-${index}`}
      role="option"
      aria-selected={isActive}
      selected={isActive}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      sx={{
        py: 0.75,
        px: 2,
        '&.Mui-selected': {
          backgroundColor: 'action.selected',
        },
        '&.Mui-selected:hover': {
          backgroundColor: 'action.selected',
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
        {result.icon || CATEGORY_ICONS[result.category]}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" noWrap sx={{ flexShrink: 1, minWidth: 0 }}>
              {result.title}
            </Typography>
            {result.badge && (
              <Chip
                label={result.badge}
                size="small"
                variant="outlined"
                color={
                  result.badge === 'Pinned' ? 'primary'
                    : result.badge === 'Today' ? 'success'
                    : result.badge === 'My Case' ? 'info'
                    : 'default'
                }
                sx={{ height: 20, fontSize: '0.65rem', flexShrink: 0 }}
              />
            )}
          </Box>
        }
        secondary={
          result.subtitle ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {result.subtitle}
            </Typography>
          ) : null
        }
      />
    </ListItemButton>
  );
};

export default CommandPaletteResultItem;
